import React from 'react';
import {
  EnvelopeSimple,
  ChatCircle,
  PencilSimple,
} from '@phosphor-icons/react';
import { cn } from 'src/utils/className';
import {
  PostCallActionType,
  PostCallFinalizedAction,
  PostCallEmailConfig,
  PostCallSmsConfig,
} from 'src/interfaces/agent.interface';

interface PostCallActionCardProps {
  action: PostCallFinalizedAction;
  onClick: () => void;
  disabled?: boolean;
}

export const PostCallActionCard: React.FC<PostCallActionCardProps> = ({
  action,
  onClick,
  disabled = false,
}) => {
  const isEmail = action.type === PostCallActionType.EMAIL;
  const config = action.config;
  const toValue = isEmail
    ? (config as PostCallEmailConfig).to_email
    : (config as PostCallSmsConfig).to_phone;

  const conditionCount = action.conditions?.children?.length ?? 0;

  return (
    <div
      data-testid={`card-action-${action.id}`}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'px-4 py-3 rounded-lg border border-neutral-200 bg-neutral-0',
        'transition-all duration-150',
        disabled
          ? 'opacity-60 cursor-not-allowed'
          : 'cursor-pointer hover:border-neutral-300 hover:shadow-sm',
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded', 'bg-primary-100 text-primary-500')}>
          {isEmail ? (
            <EnvelopeSimple className="w-4 h-4" weight="fill" />
          ) : (
            <ChatCircle className="w-4 h-4" weight="fill" />
          )}
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-4">
          <div className="flex items-center gap-2 min-w-0 shrink-0">
            <h3
              data-testid={`text-action-label-${action.id}`}
              className="text-body font-medium text-primary-body truncate"
            >
              {action.label || 'Untitled'}
            </h3>
          </div>
          <div className="flex items-center gap-3 text-description text-neutral-400 truncate">
            {toValue && (
              <span
                data-testid={`text-action-to-${action.id}`}
                className="truncate"
              >
                {toValue}
              </span>
            )}
            {conditionCount > 0 && (
              <span
                data-testid={`text-action-conditions-${action.id}`}
                className="shrink-0"
              >
                {conditionCount} condition{conditionCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="text-neutral-400">
          <PencilSimple size={20} />
        </div>
      </div>
    </div>
  );
};
